import { skipToken } from '@reduxjs/toolkit/query';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ZERO } from '@/constants';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  ListItemText,
  MenuItem,
  TextField,
  Typography,
} from '@/ui';

import AvailabilityTraining from './AvailabilityTraining';
import { SettingsBottom, SettingsTop } from './Settings';

import type { CoachStatus } from '@/constants/coachStatus';
import type { SeatType, User } from '@/types';
import type { FC } from 'react';

import Members from '@/components/Profile/Coach/Edit/Admin/Members';
import {
  COACH_STATUS_OPTIONS,
  coachStatusesLabel,
  sortCoachStatuses,
} from '@/constants/coachStatus';
import { usesMomentsScheduling } from '@/features/appointments/schedulingProvider';
import RemoveCoachModal from '@/pages/Member/Coaches/Remove';
import { api } from '@/store/api';
import {
  useDeleteCoachProfileMutation,
  useUpdateCoachProfileMutation,
} from '@/store/api/coachAssignmentApi';
import {
  useGetCoachStatusQuery,
  useUpdateCoachStatusMutation,
  useUpdateUserMutation,
  useUpdateUserProfileMutation,
} from '@/store/api/userApi';
import { useAppDispatch } from '@/store/hooks';

const GENDERS = ['Male', 'Female', 'Non-binary'] as const;

const displayGender = (profile?: User['coachProfile']) => profile?.gender ?? '';
const genderPayloadValue = (value: string) => value || null;

interface EditCoachAdminProps {
  canManageCoachStatus: boolean;
  coach: User;
  onClose: (updated?: Partial<User>) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- we don't actually use the result
  refetchCoach: () => Promise<any>;
}

const normalizeSeatRates = (
  rates?: Record<string, number>,
): Partial<Record<SeatType, number>> => {
  const result: Partial<Record<SeatType, number>> = {};
  if (!rates) return result;
  Object.entries(rates).forEach(([key, value]) => {
    const snakeKey = key.includes('_')
      ? key
      : key.replace(/([A-Z])/gu, '_$1').toLowerCase();
    if (typeof value === 'number') result[snakeKey as SeatType] = value;
  });
  return result;
};

const isVacationRangeValid = (
  hasVacation: boolean,
  startDate: string | null,
  endDate: string | null,
  startDateError: boolean,
  endDateError: boolean,
) =>
  !hasVacation ||
  (!startDateError &&
    !endDateError &&
    Boolean(startDate) &&
    Boolean(endDate) &&
    startDate! <= endDate!);

const EditCoachAdmin: FC<EditCoachAdminProps> = ({
  canManageCoachStatus,
  coach,
  refetchCoach,
  onClose,
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [pendingRemove, setPendingRemove] = useState(false);
  const [confirmingDeactivation, setConfirmingDeactivation] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState<
    CoachStatus[] | null
  >(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [statusUpdated, setStatusUpdated] = useState(false);

  const [isActive, setIsActive] = useState(coach.isActive);
  const [capacity, setCapacity] = useState(
    coach.coachProfile?.capacity || ZERO,
  );
  const [startDate, setStartDate] = useState<string | null>(
    coach.coachProfile?.startDate ?? null,
  );
  const [terminationDate, setTerminationDate] = useState<string | null>(
    coach.coachProfile?.terminationDate ?? null,
  );
  const [gender, setGender] = useState(displayGender(coach.coachProfile));
  const [timezone, setTimezone] = useState<string | null>(
    coach.profile?.timezone ?? null,
  );
  const [vacationStartDate, setVacationStartDate] = useState<string | null>(
    coach.coachProfile?.vacationStartDate ?? null,
  );
  const [vacationEndDate, setVacationEndDate] = useState<string | null>(
    coach.coachProfile?.vacationEndDate ?? null,
  );
  const [hasVacation, setHasVacation] = useState(
    Boolean(vacationStartDate || vacationEndDate),
  );
  const [vacationStartDateError, setVacationStartDateError] = useState(false);
  const [vacationEndDateError, setVacationEndDateError] = useState(false);
  const [executiveTrainingCompleted, setExecutiveTrainingCompleted] = useState(
    coach.coachProfile?.executiveTrainingCompleted ?? false,
  );
  const [seatTypeRates, setSeatTypeRates] = useState<
    Partial<Record<SeatType, number | null>>
  >(
    normalizeSeatRates(
      coach.coachProfile?.seatTypeRates as unknown as Record<string, number>,
    ),
  );

  const [updateUser] = useUpdateUserMutation();
  const [updateUserProfile] = useUpdateUserProfileMutation();
  const [updateCoachProfile] = useUpdateCoachProfileMutation();
  const [deleteCoachProfile] = useDeleteCoachProfileMutation();
  const {
    data: coachStatusData,
    isError: isCoachStatusError,
    isLoading: isCoachStatusLoading,
  } = useGetCoachStatusQuery(canManageCoachStatus ? coach.id : skipToken);
  const [updateCoachStatus, { isLoading: isUpdatingStatus = false } = {}] =
    useUpdateCoachStatusMutation();

  useEffect(() => {
    const loadedStatuses = coachStatusData?.coachStatus?.states;
    if (selectedStatuses === null && loadedStatuses)
      setSelectedStatuses(sortCoachStatuses(loadedStatuses));
  }, [coachStatusData, selectedStatuses]);

  const onActiveChange = (value: boolean) => setIsActive(value);
  const onStatusUpdate = async () => {
    if (!canManageCoachStatus || !selectedStatuses?.length) return;
    setStatusError(null);
    setStatusUpdated(false);
    try {
      await updateCoachStatus({
        id: coach.id,
        states: selectedStatuses,
      }).unwrap();
      setStatusUpdated(true);
    } catch (error) {
      const message =
        error &&
        typeof error === 'object' &&
        typeof (error as { message?: unknown }).message === 'string'
          ? (error as { message: string }).message
          : 'Unable to update status. Please try again.';
      setStatusError(message);
    }
  };
  const onCapacityChange = (value = ZERO) => setCapacity(value);
  const onStartDateChange = (value: string | null) => setStartDate(value);
  const onTerminationDateChange = (value: string | null) =>
    setTerminationDate(value);
  const onSeatTypeRateChange = (
    seatType: SeatType,
    value: number | null = ZERO,
  ) => setSeatTypeRates((prev) => ({ ...prev, [seatType]: value }));
  const reconcileCoach = (refreshedCoach: User) => {
    const refreshedProfile = refreshedCoach.coachProfile;
    const refreshedVacationStartDate =
      refreshedProfile?.vacationStartDate ?? null;
    const refreshedVacationEndDate = refreshedProfile?.vacationEndDate ?? null;
    setIsActive(refreshedCoach.isActive);
    setCapacity(refreshedProfile?.capacity ?? ZERO);
    setStartDate(refreshedProfile?.startDate ?? null);
    setTerminationDate(refreshedProfile?.terminationDate ?? null);
    setGender(displayGender(refreshedProfile));
    setSeatTypeRates(
      normalizeSeatRates(
        refreshedProfile?.seatTypeRates as unknown as Record<string, number>,
      ),
    );
    setTimezone(refreshedCoach.profile?.timezone ?? null);
    setVacationStartDate(refreshedVacationStartDate);
    setVacationEndDate(refreshedVacationEndDate);
    setHasVacation(
      Boolean(refreshedVacationStartDate || refreshedVacationEndDate),
    );
    setVacationStartDateError(false);
    setVacationEndDateError(false);
    setExecutiveTrainingCompleted(
      refreshedProfile?.executiveTrainingCompleted ?? false,
    );
  };

  const onSave = async () => {
    setIsSaving(true);
    setSaveError(false);
    const payloadSeatTypeRates = Object.fromEntries(
      Object.entries(seatTypeRates).map(([k, v]) => [
        k,
        v === null ? null : Number(v),
      ]),
    );
    const settlements = await Promise.allSettled([
      updateUser({ id: coach.id, user: { isActive } }).unwrap(),
      updateUserProfile({
        id: coach.id,
        userProfile: { timezone },
      }).unwrap(),
      updateCoachProfile({
        coachProfile: {
          capacity,
          executiveTrainingCompleted,
          gender: genderPayloadValue(gender),
          seatTypeRates: payloadSeatTypeRates,
          startDate,
          terminationDate,
          vacationEndDate: hasVacation ? vacationEndDate : null,
          vacationStartDate: hasVacation ? vacationStartDate : null,
        },
        id: coach.id,
      }).unwrap(),
    ]);
    const allSucceeded = settlements.every(
      (settlement) => settlement.status === 'fulfilled',
    );
    try {
      const refreshed = await refetchCoach();
      if (allSucceeded) onClose();
      else {
        const refreshedCoach = refreshed?.data as User | undefined;
        if (refreshedCoach) reconcileCoach(refreshedCoach);
        setSaveError(true);
      }
    } catch {
      setSaveError(true);
    } finally {
      setIsSaving(false);
    }
  };
  // MEN-4183: deactivating a coach cancels every future Mento Calendar session they have, which
  // a coach still routed to Cal can also have, so every deactivation is confirmed. Cal.com
  // bookings are not cancelled and still need Ops cleanup.
  const routedToMoments = usesMomentsScheduling(coach);
  const onSaveClick = async () => {
    if (coach.isActive && !isActive) setConfirmingDeactivation(true);
    else await onSave();
  };
  const onConfirmDeactivation = async () => {
    setConfirmingDeactivation(false);
    await onSave();
  };
  const onRemove = async () => {
    setIsRemoving(true);
    await deleteCoachProfile(coach.id).unwrap();

    dispatch(api.util.invalidateTags(['User'])); // Coach profiles are Users
    setIsRemoving(false);

    navigate('/coach/manage/coaches');
  };

  const onCancel = () => onClose();
  const onPendingRemove = () => setPendingRemove(true);
  const onConfirmRemove = async () => {
    await onRemove();
    setPendingRemove(false);
  };
  const onCancelRemove = () => setPendingRemove(false);
  const onHasVacationChange = (value: boolean) => {
    setHasVacation(value);
    if (!value) {
      setVacationStartDate(null);
      setVacationEndDate(null);
      setVacationStartDateError(false);
      setVacationEndDateError(false);
    }
  };
  const vacationIsValid = isVacationRangeValid(
    hasVacation,
    vacationStartDate,
    vacationEndDate,
    vacationStartDateError,
    vacationEndDateError,
  );

  return (
    <Dialog maxWidth="md" fullWidth open>
      <DialogContent>
        {saveError && (
          <Alert
            aria-label="Unable to save changes"
            className="mb-6"
            severity="error"
          >
            Unable to save changes. Review the latest values and try again.
          </Alert>
        )}
        <SettingsTop
          calendarUrl={coach.coachProfile?.calendarUrl}
          capacity={capacity}
          email={coach.email}
          isActive={!!isActive}
          schedulingProvider={coach.coachProfile?.schedulingProvider}
          onActivateChange={onActiveChange}
          onCapacityChange={onCapacityChange}
          onRemove={onPendingRemove}
        />
        {canManageCoachStatus && (
          <>
            <Divider className="my-8" />
            <Grid spacing={4} container>
              <Grid md={5} xs={12} item>
                <Typography className="mb-2" variant="h4">
                  Coach status
                </Typography>
                <Typography variant="body1">
                  Status controls matching eligibility independently from
                  account access.
                </Typography>
              </Grid>
              <Grid className="mt-1 flex flex-col gap-3" md={7} xs={12} item>
                <TextField
                  disabled={
                    isCoachStatusLoading ||
                    isCoachStatusError ||
                    isUpdatingStatus
                  }
                  error={!!statusError || isCoachStatusError}
                  helperText={
                    statusError ??
                    (isCoachStatusError ? 'Unable to load status.' : undefined)
                  }
                  label="Status"
                  SelectProps={{
                    displayEmpty: true,
                    multiple: true,
                    renderValue: (value) =>
                      isCoachStatusLoading
                        ? 'Loading...'
                        : coachStatusesLabel(value as CoachStatus[]),
                  }}
                  value={selectedStatuses ?? []}
                  required
                  select
                  onChange={(event) => {
                    const { value } = event.target as { value: unknown };
                    setSelectedStatuses(
                      sortCoachStatuses(value as CoachStatus[]),
                    );
                    setStatusError(null);
                    setStatusUpdated(false);
                  }}
                >
                  {COACH_STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      <Checkbox
                        checked={!!selectedStatuses?.includes(option.id)}
                        size="small"
                      />
                      <ListItemText primary={option.name} />
                    </MenuItem>
                  ))}
                </TextField>
                <div className="flex items-center gap-3">
                  <Button
                    disabled={!selectedStatuses?.length || isCoachStatusError}
                    loading={isUpdatingStatus}
                    variant="contained"
                    onClick={onStatusUpdate}
                  >
                    Update status
                  </Button>
                  {statusUpdated && (
                    <Typography className="text-green-700" variant="body2">
                      Status updated.
                    </Typography>
                  )}
                </div>
              </Grid>
            </Grid>
          </>
        )}
        <Divider className="my-8" />
        <Members coach={coach} refetchCoach={refetchCoach} />
        <Divider className="my-8" />
        <SettingsBottom
          seatTypeRates={seatTypeRates}
          startDate={startDate}
          terminationDate={terminationDate}
          onSeatTypeRateChange={onSeatTypeRateChange}
          onStartDateChange={onStartDateChange}
          onTerminationDateChange={onTerminationDateChange}
        />
        <Divider className="my-8" />
        <Grid spacing={4} container>
          <Grid md={5} xs={12} item>
            <Typography className="mb-2" variant="h4">
              Coach demographics
            </Typography>
            <Typography variant="body1">
              Store coach demographic fields for analytics and client
              preferences. These fields are not primary matching filters.
            </Typography>
          </Grid>
          <Grid className="mt-1 flex flex-col gap-4" md={7} xs={12} item>
            <TextField
              className="text-left"
              label="Gender"
              value={gender}
              select
              onChange={(event) => setGender(event.target.value)}
            >
              <MenuItem value="">Not set</MenuItem>
              {GENDERS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
        <Divider className="my-8" />
        <AvailabilityTraining
          executiveTrainingCompleted={executiveTrainingCompleted}
          hasVacation={hasVacation}
          timezone={timezone}
          vacationEndDate={vacationEndDate}
          vacationEndDateError={vacationEndDateError}
          vacationStartDate={vacationStartDate}
          vacationStartDateError={vacationStartDateError}
          onExecutiveTrainingChange={setExecutiveTrainingCompleted}
          onHasVacationChange={onHasVacationChange}
          onTimezoneChange={setTimezone}
          onVacationEndDateChange={setVacationEndDate}
          onVacationEndDateError={setVacationEndDateError}
          onVacationStartDateChange={setVacationStartDate}
          onVacationStartDateError={setVacationStartDateError}
        />
      </DialogContent>
      <DialogActions>
        <Button color="primary" variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          color="primary"
          disabled={!vacationIsValid}
          loading={isSaving}
          variant="contained"
          onClick={onSaveClick}
        >
          Save
        </Button>
      </DialogActions>
      <Dialog open={confirmingDeactivation}>
        <Typography className="px-4 py-2" variant="h5">
          Disable coach account
        </Typography>
        <Typography className="px-4 py-2 text-gray-500">
          {routedToMoments
            ? "All future coaching sessions will be deleted from Coach's and Members' calendars. This action cannot be undone."
            : "All future Mento Calendar sessions will be deleted from Coach's and Members' calendars. Cal.com bookings are not cancelled and must be cancelled manually. This action cannot be undone."}
        </Typography>
        <DialogActions className="mt-2">
          <Button
            variant="outlined"
            onClick={() => setConfirmingDeactivation(false)}
          >
            Keep account enabled
          </Button>
          <Button
            className="ml-3"
            color="error"
            variant="contained"
            onClick={onConfirmDeactivation}
          >
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>
      <RemoveCoachModal
        close={onCancelRemove}
        isOpen={pendingRemove}
        processing={isRemoving}
        onRemove={onConfirmRemove}
      />
    </Dialog>
  );
};

export default EditCoachAdmin;
